/* eslint-disable max-lines */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGames } from "../store/slices/gameSlice";
import { getHotGames } from "../store/slices/userActions";
import HomeSlotSection from "../components/HomeSlotSection";
import HomeCasinoSection from "../components/HomeCasinoSection";
import axios from "axios";
import { getPopupList } from "../store/slices/user";
import CounterBonus from "../components/CounterBonus";
import CheckinExperience from "../components/CheckinExperience";
import { buildGameStartPayload } from "../utils/gameStartPayload";

// Use API URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let homeDataFetched = false;

const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case "info":
        return "ℹ️";
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      default:
        return "⚠️";
    }
  };

  const getColors = () => {
    switch (type) {
      case "info":
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
      case "warning":
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
      case "error":
        return {
          bg: "#FEE2E2",
          border: "#EF4444",
          text: "#991B1B",
        };
      default:
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "400px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>{getIcon()}</span>
      <span style={{ color: colors.text, fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "18px",
          cursor: "pointer",
          color: colors.text,
          padding: "0",
          marginLeft: "8px",
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

const POPUP_DISPLAY_MODE = {
  ALWAYS: "ALWAYS",
  SCHEDULED: "SCHEDULED",
};

const now = () => new Date();

const isWithinSchedule = (popup) => {
  if (!popup || popup.displayMode !== POPUP_DISPLAY_MODE.SCHEDULED) {
    return true;
  }

  const start = popup.scheduleStart ? new Date(popup.scheduleStart) : null;
  const end = popup.scheduleEnd ? new Date(popup.scheduleEnd) : null;
  const current = now();

  if (start && Number.isNaN(start.getTime())) return false;
  if (end && Number.isNaN(end.getTime())) return false;
  if (start && current < start) return false;
  if (end && current > end) return false;
  return true;
};

const sortPopupQueue = (popupList) => {
  const scheduled = [];
  const always = [];
  popupList.forEach((popup) => {
    if (popup.displayMode === POPUP_DISPLAY_MODE.SCHEDULED) {
      scheduled.push(popup);
    } else {
      always.push(popup);
    }
  });

  scheduled.sort((a, b) => {
    const startA = a.scheduleStart ? new Date(a.scheduleStart).getTime() : 0;
    const startB = b.scheduleStart ? new Date(b.scheduleStart).getTime() : 0;
    return startA - startB;
  });

  always.sort((a, b) => (a.id || 0) - (b.id || 0));

  return [...scheduled, ...always];
};

const Home = () => {
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [isLoadingHotGames, setIsLoadingHotGames] = useState(false);
  const dispatch = useDispatch();
  const { gameList, loading, error } = useSelector((state) => state.games);
  const {
    hotGames: hotGamesData,
    singleUsers,
    allPopup,
  } = useSelector((state) => state.user);
  const playerBalance = useSelector((state) => state.user.playerBalance);

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  // Popup state for post-login banners
  const [openPopup, setOpenPopup] = useState(false);
  const [validPopupImageList, setValidPopupImageList] = useState([]);
  // Use localStorage to track popup shown per user
  const getPopupShownKey = (userId) => `popup_shown_${userId}`;
  const prevUserIdRef = useRef(null);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  useEffect(() => {
    if (homeDataFetched) return;
    homeDataFetched = true;
    dispatch(fetchGames());
    dispatch(getHotGames());
  }, [dispatch]);

  const currentUser = useMemo(() => {
    if (!singleUsers) return null;
    if (Array.isArray(singleUsers)) {
      return singleUsers[0] || null;
    }
    if (Array.isArray(singleUsers?.data)) {
      return singleUsers.data[0] || null;
    }
    return singleUsers;
  }, [singleUsers]);

  // Group hot games by provider
  const hotGamesByProvider = React.useMemo(() => {
    if (!hotGamesData || hotGamesData.length === 0) return {};

    return hotGamesData.reduce((acc, game) => {
      if (!acc[game.productId]) {
        acc[game.productId] = [];
      }
      acc[game.productId].push({
        title: game.gameName,
        imageUrl: game.imageUrl,
        userCount: game.onlineUsers,
        rtp: game.rtpPercent,
        gameCode: game.gameCode,
      });
      return acc;
    }, {});
  }, [hotGamesData]);

  // Get unique providers from hot games, using backend fields
  const hotProviders = React.useMemo(() => {
    if (!hotGamesData || hotGamesData.length === 0) return [];

    // Use productId, productName, logo from backend
    const uniqueProviders = [];
    const seen = new Set();
    hotGamesData.forEach((game) => {
      if (!seen.has(game.productId)) {
        seen.add(game.productId);
        uniqueProviders.push({
          id: uniqueProviders.length,
          name: game.productId,
          displayName: game.productName || game.productId,
          imageUrl:
            game.imageUrl ||
            `https://space.kuga59.co/${game.productId}-menu.webp`,
        });
      }
    });
    return uniqueProviders;
  }, [hotGamesData]);

  // Use hot games data if available, otherwise fallback to static data
  const providers = React.useMemo(() => {
    return hotProviders.length > 0
      ? hotProviders
      : [
          { id: 0, name: "PG", displayName: "PG", logo: "pg-cartoon-menu.png" },
          { id: 1, name: "AMBPOKER", logo: "ambpoker-menu-cartoon.png" },
          { id: 2, name: "AMBSLOT", logo: "ambslot-menu-cartoon.png" },
          { id: 3, name: "ASKMEBET", logo: "askmebet-menu-cartoon.png" },
          { id: 4, name: "HACKJSAW", logo: "hacksaw-menu-cartoon.png" },
          { id: 5, name: "PRETTY", logo: "pretty-menu-cartoon.png" },
          { id: 6, name: "PP", logo: "pp-menu-cartoon.png" },
          { id: 7, name: "AMBGAMING", logo: "ambgaming-menu-cartoon.png" },
          { id: 8, name: "SEXY", logo: "sexyslot-menu-cartoon.png" },
        ];
  }, [hotProviders]);

  const games = React.useMemo(() => {
    return hotGamesByProvider[providers[selectedProvider]?.name] || [];
  }, [hotGamesByProvider, providers, selectedProvider]);

  // Add random variation to user count for each game
  const gamesWithRandomUsers = React.useMemo(() => {
    return games.map((game) => ({
      ...game,
      displayUserCount: Math.max(
        0,
        game.userCount + Math.floor(Math.random() * 11) - 5,
      ), // Random ±5
    }));
  }, [games]);

  // Prepare popup lists when allPopup changes
  const popupData = useMemo(() => {
    return (allPopup || []).filter(
      (popup) => popup.popupStatus === "NORMAL" && isWithinSchedule(popup),
    );
  }, [allPopup]);

  const popupQueue = useMemo(() => sortPopupQueue(popupData), [popupData]);

  const popupImageList = useMemo(() => {
    return popupQueue.map((item) => ({
      id: item.id,
      mode: item.displayMode,
      src: `${API_BASE_URL}${item?.imageUrl}`,
    }));
  }, [popupQueue]);

  useEffect(() => {
    setValidPopupImageList(popupImageList);
  }, [popupImageList]);

  // When user logs in, fetch popup list and show popup only once per login
  useEffect(() => {
    const prevId = prevUserIdRef.current;

    if (currentUser) {
      const popupKey = getPopupShownKey(currentUser.id);
      const hasShown = localStorage.getItem(popupKey) === "true";

      if (!hasShown) {
        dispatch(getPopupList());
        setTimeout(() => {
          setOpenPopup(true);
        }, 1500);
      } else {
        setOpenPopup(false);
      }
    } else {
      if (prevId) {
        try {
          localStorage.removeItem(getPopupShownKey(prevId));
        } catch (_) {}
      }
      setOpenPopup(false);
    }

    prevUserIdRef.current = currentUser?.id || null;
  }, [currentUser, dispatch]);

  const handleClosePopup = useCallback(() => {
    if (!validPopupImageList.length) {
      setOpenPopup(false);
      return;
    }

    const [, ...next] = validPopupImageList;
    setValidPopupImageList(next);
    const shouldClose = next.length === 0;
    setOpenPopup(!shouldClose);

    if (shouldClose && currentUser) {
      const popupKey = getPopupShownKey(currentUser.id);
      try {
        localStorage.setItem(popupKey, "true");
      } catch (_) {}
    }
  }, [currentUser, validPopupImageList]);

  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || "ALLPROVIDER";
  const providerAllowanceList =
    providerAllowanceRaw === "ALLPROVIDER"
      ? null
      : providerAllowanceRaw.split(",").map((s) => s.trim().toLowerCase());

  const isProviderAllowed = useCallback(
    (prov) => {
      if (!currentUser) return true;
      if (providerAllowanceList === null) return true;

      const candidates = [];
      if (!prov) return false;
      if (typeof prov === "string") {
        candidates.push(prov);
      } else {
        candidates.push(prov.productId, prov.productName, prov.name, prov.id);
      }

      const normalized = candidates
        .filter((c) => c !== undefined && c !== null && c !== "")
        .map((c) => String(c).toLowerCase());

      return normalized.some((n) => providerAllowanceList?.includes(n));
    },
    [currentUser, providerAllowanceList],
  );

  const allowedProviders = React.useMemo(() => {
    if (!currentUser) return providers;
    return providers.filter((prov) => isProviderAllowed(prov));
  }, [currentUser, providers, isProviderAllowed]);

  useEffect(() => {
    if (!allowedProviders.length) return;
    const current = providers.find((p) => p.id === selectedProvider);
    if (!current || (currentUser && !isProviderAllowed(current))) {
      setSelectedProvider(allowedProviders[0].id);
    }
  }, [
    allowedProviders,
    currentUser,
    providers,
    selectedProvider,
    isProviderAllowed,
  ]);

  const startGame = async ({ provider, gamecode }) => {
    if (!provider || !gamecode) return;

    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch (_) {}

    const data = buildGameStartPayload({
      data: { id: userId, provider, gamecode },
      playerBalance,
    });
    const gameWindow = window.open("", "_blank");

    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: { data },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        if (gameWindow) {
          gameWindow.location.href = res.data;
        } else {
          window.location.href = res.data;
        }
      }
    } catch (error) {
      console.error(error);
      if (gameWindow) gameWindow.close();
      showToast("เกิดข้อผิดพลาดในการเข้าสู่เกมส์", "error");
    }
  };

  const handleTabChangeWithCheck = (id) => {
    const sourceList = currentUser ? allowedProviders : providers;
    const prov =
      sourceList.find((p) => p.id === id) || providers.find((p) => p.id === id);
    if (!prov) {
      setSelectedProvider(id);
      return;
    }

    if (!currentUser) {
      setIsLoadingHotGames(true);
      setSelectedProvider(id);
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingHotGames(false);
      }, 300);
      return;
    }

    if (isProviderAllowed(prov)) {
      setIsLoadingHotGames(true);
      setSelectedProvider(id);
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingHotGames(false);
      }, 300);
    } else {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
    }
  };

  // add type allowance parsing and provider click handler for slot/casino
  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? currentUser.gameTypeAvailable
        .split(",")
        .map((s) => s.trim().toLowerCase())
    : ["allgametype"];

  const isTypeAllowedFor = (type) => {
    const map = {
      slot: "slots",
      casino: "livecasino",
      fishing: "fishing",
      sport: "sportbook",
    };
    const key = map[type] || type;
    return (
      typeAllowanceList.includes("allgametype") ||
      typeAllowanceList.includes(key) ||
      typeAllowanceList.includes(type)
    );
  };

  const remoteProviderPrefixes = [
    "bs",
    "ng",
    "aws",
    "swg",
    "km",
    "fc",
    "mg",
    "gamatron",
    "ep",
    "pp",
    "netent",
    "joker",
    "bpg",
    "bng",
    "hab",
    "kgl",
    "rlx",
    "ygg",
    "red",
    "qs",
    "ids",
    "tk",
    "ds",
    "nlc",
    "ga",
    "kg",
    "png",
    "pug",
    "fng",
    "nge",
    "1x2",
    "hak",
    "waz",
    "elk",
    "prs",
    "jl",
    "sn",
    "dgs",
  ];

  const handleProviderClick = async (provider, type = "slot") => {
    if (!currentUser) {
      try {
        window.dispatchEvent(new CustomEvent("open-login-modal"));
      } catch (e) {
        // ignore
      }
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor(type)) {
      let msg = "ท่านไม่สามารถเล่นประเภทเกมส์นี้ได้ เนื่องจากติดเงื่อนไข";
      if (type === "slot")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์สล็อตนี้ได้ เนื่องจากติดเงื่อนไข";
      else if (type === "casino")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์คาสิโนนี้ได้ เนื่องจากติดเงื่อนไข";
      else if (type === "fishing")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์ยิงปลาได้ เนื่องจากติดเงื่อนไข";
      else if (type === "sport")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์กีฬาได้ เนื่องจากติดเงื่อนไข";
      showToast(msg, "warning");
      return;
    }

    if (!isProviderAllowed(provider)) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    const providerIdentifier =
      provider?.productId ||
      provider?.game ||
      provider?.productName ||
      provider?.name;
    const providerCode = (providerIdentifier || "").toLowerCase();
    const providerName = (
      provider?.name ||
      provider?.productName ||
      ""
    ).toLowerCase();
    const shouldStartDirect =
      !remoteProviderPrefixes.includes(providerCode) &&
      !remoteProviderPrefixes.includes(providerName);

    if (shouldStartDirect) {
      await startGame({
        provider: providerIdentifier,
        gamecode: providerIdentifier,
      });
      return;
    }

    await startGame({
      provider: providerIdentifier,
      gamecode: providerIdentifier,
    });
  };

  const handleHotGameClick = async (game) => {
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor("slot")) {
      showToast(
        "ท่านไม่สามารถเล่นประเภทเกมส์สล็อตนี้ได้ เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    const currentProviderName = providers[selectedProvider]?.name;
    if (!isProviderAllowed({ name: currentProviderName })) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    await startGame({
      provider: currentProviderName,
      gamecode: game.gameCode,
    });
  };

  const shouldShowHotSection =
    hotProviders.length > 0 && allowedProviders.length > 0;

  return (
    <div
      style={{
        padding: "18px 0 40px",
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      <CounterBonus />
      <CheckinExperience currentUser={currentUser} showToast={showToast} />

      {/* เกมส์มาแรง section only if hotProviders has data */}
      {shouldShowHotSection && (
        <div
          style={{
            margin: "0 18px 18px 18px",
            padding: "24px 16px",
            border:
              "2px solid color-mix(in srgb, var(--primaryColor) 40%, transparent)",
            borderRadius: "18px",
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            boxShadow:
              "0 4px 20px color-mix(in srgb, var(--primaryColor) 20%, transparent)",
          }}
        >
          <h1
            style={{
              color: "#0e6c5e",
              textAlign: "center",
              fontSize: "28px",
              fontWeight: "800",
              margin: "12px 0 20px 0",
            }}
          >
            <Icon icon="noto:fire" className="header-fire" />
            <Icon icon="noto:fire" className="header-fire hide-mobile" />
            <Icon icon="noto:fire" className="header-fire" /> เกมส์มาแรง{" "}
            <Icon icon="noto:fire" className="header-fire" />
            <Icon icon="noto:fire" className="header-fire hide-mobile" />
            <Icon icon="noto:fire" className="header-fire" />
          </h1>
          <style>
            {`
              @media (max-width: 768px) {
                div[style*="padding: 32px"] {
                  padding: 24px !important;
                }
                
                h1 {
                  font-size: 24px; /* Smaller size for mobile */
                }
                h1 .iconify {
                  font-size: 24px; /* Adjust icon size for mobile */
                }
                .hide-mobile { display: none !important; }
              }
                 @media (min-width: 768px) {
                h1 {
                  font-size: 32px; /* Smaller size for mobile */
                }
                h1 .iconify {
                  font-size: 32px; /* Adjust icon size for mobile */
                }
              }
            `}
          </style>

          {/* Tabs: playable providers first, then all providers */}
          <div
            style={{
              marginTop: "16px",
              background: "transparent",
              padding: "12px 0",
              borderRadius: "12px",
            }}
          >
            {currentUser ? (
              <div
                className="provider-tabs"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {allowedProviders.map((prov) => (
                  <img
                    key={prov.id}
                    src={`https://space.kuga59.co/menu-cartoon/${prov.name}.webp`}
                    alt={prov.displayName}
                    onClick={() => handleTabChangeWithCheck(prov.id)}
                    onError={(e) => {
                      if (e.target.src.endsWith(".webp")) {
                        e.target.src = `https://space.kuga59.co/menu-cartoon/${prov.name}.gif`;
                      }
                    }}
                    className="provider-logo"
                    style={{
                      width: "104px",
                      height: "104px",
                      cursor: "pointer",
                      opacity: selectedProvider === prov.id ? 1 : 0.3,
                    }}
                  />
                ))}
              </div>
            ) : (
              <div
                className="provider-tabs"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {providers.map((prov) => (
                  <img
                    key={prov.id}
                    src={`https://space.kuga59.co/menu-cartoon/${prov.name}.webp`}
                    alt={prov.displayName}
                    onClick={() => handleTabChangeWithCheck(prov.id)}
                    onError={(e) => {
                      if (e.target.src.endsWith(".webp")) {
                        e.target.src = `https://space.kuga59.co/menu-cartoon/${prov.name}.gif`;
                      }
                    }}
                    className="provider-logo"
                    style={{
                      width: "104px",
                      height: "104px",
                      cursor: "pointer",
                      opacity: selectedProvider === prov.id ? 1 : 0.4,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Game Grid */}
          <div
            className="game-grid"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "center",
              marginTop: "24px",
              minHeight: "200px",
              padding: "16px",
              background: "rgba(255, 255, 255, 0.5)",
              borderRadius: "16px",
            }}
          >
            {isLoadingHotGames ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  padding: "40px",
                  color: "#aaa",
                  fontSize: "16px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "4px solid rgba(255, 255, 255, 0.1)",
                      borderTop: "4px solid var(--primaryColor)",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      margin: "0 auto 16px",
                    }}
                  />
                  <div>กำลังโหลดเกม...</div>
                </div>
              </div>
            ) : gamesWithRandomUsers.length > 0 ? (
              gamesWithRandomUsers.map((game, idx) => (
                <div
                  key={idx}
                  className="game-card"
                  onClick={() => handleHotGameClick(game)}
                  style={{
                    width: "180px",
                    position: "relative",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "#ffffff",
                    border:
                      "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                    boxShadow:
                      "0 4px 12px color-mix(in srgb, var(--primaryColor) 25%, transparent)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                    e.currentTarget.style.borderColor = "var(--primaryColor)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px color-mix(in srgb, var(--primaryColor) 25%, transparent)";
                    e.currentTarget.style.borderColor =
                      "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                  }}
                >
                  <div style={{ position: "relative", overflow: "hidden" }}>
                    <img
                      src={game.imageUrl}
                      alt={game.title}
                      style={{
                        width: "100%",
                        display: "block",
                        transition: "transform 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                    <span
                      className="game-card-badge user-count-badge"
                      style={{
                        position: "absolute",
                        top: "8px",
                        left: "8px",
                        background:
                          "linear-gradient(135deg, rgba(255,87,34,0.95) 0%, rgba(244,67,54,0.95) 100%)",
                        color: "#fff",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      <Icon
                        icon="fluent-color:people-team-16"
                        style={{
                          animation: "blink 1s infinite",
                          fontSize: "14px",
                        }}
                      />
                      {game.displayUserCount}
                    </span>
                    <span
                      className="game-card-badge rtp-badge"
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        background:
                          "linear-gradient(135deg, rgba(76,175,80,0.95) 0%, rgba(56,142,60,0.95) 100%)",
                        color: "#fff",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      RTP {game.rtp}%
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "12px 8px",
                      textAlign: "center",
                      background:
                        "linear-gradient(to bottom, color-mix(in srgb, var(--primaryColor) 12%, white), color-mix(in srgb, var(--primaryColor) 6%, white))",
                    }}
                  >
                    <div className="game-card-info">
                      <span
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,87,34,0.95) 0%, rgba(244,67,54,0.95) 100%)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        }}
                      >
                        <Icon
                          icon="fluent-color:people-team-16"
                          style={{
                            animation: "blink 1s infinite",
                            fontSize: "14px",
                          }}
                        />
                        {game.displayUserCount}
                      </span>
                      <span
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(76,175,80,0.95) 0%, rgba(56,142,60,0.95) 100%)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        }}
                      >
                        RTP {game.rtp}%
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "800",
                        color:
                          "color-mix(in srgb, var(--primaryColor) 85%, black)",
                        lineHeight: "1.3",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        minHeight: "34px",
                        textShadow: "none",
                      }}
                      title={game.title}
                    >
                      {game.title}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  padding: "40px",
                  color: "#0e6c5e",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                ไม่มีเกมในค่ายนี้
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slot and Casino Sections Container */}
      <div
        style={{
          margin: "0 18px 18px 18px",
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "32px",
          border: "1px solid #b7e3d8",
          borderRadius: "18px",
          background: "rgba(233, 247, 244, 0.85)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Slot Games Section moved to component */}
        <HomeSlotSection
          gameList={gameList}
          onProviderClick={handleProviderClick}
        />

        {/* Casino Games Section moved to component */}
        <HomeCasinoSection
          gameList={gameList}
          loading={loading}
          error={error}
          onProviderClick={handleProviderClick}
        />
      </div>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />

      {/* Popup Dialog shown after login (custom, no external deps) */}
      {openPopup && validPopupImageList.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            padding: 20,
          }}
          onClick={handleClosePopup}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 1000,
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <button
              aria-label="close"
              onClick={handleClosePopup}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "#e53935",
                color: "#fff",
                border: 0,
                borderRadius: "50%",
                width: 40,
                height: 40,
                cursor: "pointer",
                zIndex: 1500,
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ✕
            </button>

            <img
              src={validPopupImageList[0]?.src || validPopupImageList[0]}
              alt="popup"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
              onError={() => setValidPopupImageList((prev) => prev.slice(1))}
            />
            {/* Removed 'Don't show again' checkbox to always rely on per-login flag */}
          </div>
        </div>
      )}

      <style>
        {`
          .game-card-info {
            display: none;
          }
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }


          
          @media (max-width: 768px) {
            div[style*="maxWidth: 920px"] {
              padding: 16px !important;
            }
            .game-card-badge {
              display: none !important;
            }
            .game-card-info {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 6px;
              margin-bottom: 6px;
            }
            .game-card-info > span {
              padding: 2px 8px !important;
              font-size: 10px !important;
              display: inline-flex !important;
              align-items: center !important;
              gap: 4px !important;
            }
            .game-card-info .iconify {
              font-size: 12px !important;
            }
            .game-grid {
              display: grid !important;
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
              padding: 0 8px;
            }
            
            .game-card {
              width: 100% !important;
            }
            
            .provider-tabs {
              display: grid !important;
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 8px !important;
              padding: 0 8px !important;
              justify-items: center !important;
              align-items: center !important;
            }
            
            .provider-logo {
              width: 80px !important;
              height: 80px !important;
              object-fit: contain !important;
            }
            
            .slots-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }
            
            .slots-grid > div:first-child {
              grid-column: span 2 !important;
              grid-row: span 1 !important;
            }
            
            .casino-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }
            
            .casino-grid > div:first-child {
              grid-column: span 2 !important;
              grid-row: span 1 !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Home;
